import React, { useMemo } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
  getInSilicoThresholds,
  getNewInSilicoThresholds,
} from "../../../../state/selectors/general.selectors";
import { getPhyloP100WayScore } from "../../../../state/selectors/CardsUI.selectors";
import CardWrapper from "../../CardWrapper";
import Card from "../../Card";
import { getPathogenicityColorByAllNames } from "../../../../common/helpers/Constant";
import { isThaliaVersionSmallerThanElevenPointFour } from "../../../../state/selectors/ACMGComponent.selectors";
import {
  checkIfResultIsEmpty,
  getColor,
} from "../../../../pages/variant/components/ConservationScores/ConservationScoresHelpers";
import tailwindColors from "../../../../../../tailwind.colors";
import {
  getDbnsfp,
  getGerp,
  getPhastCons100Way,
  getPhyloP100Way,
} from "../../../../state/selectors/ConservationScores.selector";
import { transformThresholds } from "../../../../pages/variant/components/ConservationScores/helpers/conservationsHelpers";

const ConservationScoresCard = ({
  insilicoThreshHolds,
  phyloP100wayScore,
  card,
  shouldColorScoreBasedOnNewACMGThresholds,
  dbnsfp,
  gerp,
  phylop100,
  phastcons100,
}) => {
  const defaultColor = tailwindColors.blueGray[400];

  function containsEntries(databaseArray) {
    return Object.values(databaseArray?.[0] || {})?.filter(Boolean).length > 0;
  }

  const getIsCardDisabled = useMemo(
    () =>
      !checkIfResultIsEmpty(dbnsfp?.[0] || {}) &&
      !containsEntries(gerp) &&
      !containsEntries(phylop100) &&
      !containsEntries(phastcons100),
    [gerp, phylop100, phastcons100, dbnsfp]
  );

  const calcColor = () => {
    if (shouldColorScoreBasedOnNewACMGThresholds) {
      return getColor("phylop", phyloP100wayScore, insilicoThreshHolds);
    }

    let color = defaultColor;
    if (phyloP100wayScore > insilicoThreshHolds?.phylop_conserved) {
      color = getPathogenicityColorByAllNames("Likely Pathogenic");
    } else if (phyloP100wayScore < 0) {
      color = getPathogenicityColorByAllNames("Likely Benign");
    }
    return color;
  };

  const pathoColor = phyloP100wayScore ? calcColor() : defaultColor;

  return (
    <CardWrapper
      {...card}
      card={card}
      isDisabled={getIsCardDisabled}
      color={pathoColor}
    >
      {phyloP100wayScore ? (
        <div data-testid="score">
          phyloP100:{" "}
          <b data-testid="score-value" style={{ color: pathoColor }}>
            {phyloP100wayScore}
          </b>
        </div>
      ) : null}
    </CardWrapper>
  );
};

ConservationScoresCard.propTypes = {
  card: PropTypes.instanceOf(Card).isRequired,
  insilicoThreshHolds: PropTypes.shape({
    cadd_benign: PropTypes.number,
    cadd_pathogenic: PropTypes.number,
    dann_benign: PropTypes.number,
    dann_pathogenic: PropTypes.number,
    eigen_benign: PropTypes.number,
    eigen_pathogenic: PropTypes.number,
    mvp_benign: PropTypes.number,
    mvp_pathogenic: PropTypes.number,
    revel_benign: PropTypes.number,
    revel_pathogenic: PropTypes.number,
    sift_benign: PropTypes.number,
    sift_pathogenic: PropTypes.number,
    eigen_pc_benign: PropTypes.number,
    eigen_pc_pathogenic: PropTypes.number,
    mutPred_benign: PropTypes.number,
    mutPred_pathogenic: PropTypes.number,
    phylop_conserved: PropTypes.number,
    phylop_benign: PropTypes.number,
    phylop_pathogenic: PropTypes.number,
  }),
  phyloP100wayScore: PropTypes.number,
  shouldColorScoreBasedOnNewACMGThresholds: PropTypes.bool,
  dbnsfp: PropTypes.arrayOf(PropTypes.shape({})),
  gerp: PropTypes.arrayOf(PropTypes.shape({})),
  phylop100: PropTypes.arrayOf(PropTypes.shape({})),
  phastcons100: PropTypes.arrayOf(PropTypes.shape({})),
};

ConservationScoresCard.defaultProps = {
  phyloP100wayScore: null,
  insilicoThreshHolds: null,
  shouldColorScoreBasedOnNewACMGThresholds: false,
  dbnsfp: null,
  gerp: null,
  phylop100: null,
  phastcons100: null,
};

const mapStateToProps = (state) => ({
  dbnsfp: getDbnsfp(state),
  gerp: getGerp(state),
  phylop100: getPhyloP100Way(state),
  phastcons100: getPhastCons100Way(state),
  insilicoThreshHolds: transformThresholds(
    getInSilicoThresholds(state) || getNewInSilicoThresholds(state)
  ),
  phyloP100wayScore: getPhyloP100WayScore(state),
  shouldColorScoreBasedOnNewACMGThresholds:
    !isThaliaVersionSmallerThanElevenPointFour(state),
});

export default connect(mapStateToProps)(ConservationScoresCard);
